import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Q7 (director 13 Oct 2026): read-only dry run of the Stage-2 clinician test identities + the a327c45e throwaway. No apply button by design:
// removal, when approved, is the dashboard's own Delete -> Purge per account.
export const Stage2RecordsCard = ({ token }) => {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/cleanup/stage2/dry-run`, { headers: { Authorization: `Bearer ${token}` } });
      setRep(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Dry run failed'); } finally { setBusy(false); }
  };

  const links = (r) => Object.entries(r.links || {}).map(([k, v]) => `${k} ${v}`).join(', ') || 'none';
  const cloud = (r) => r.cloudinary ? `photos ${r.cloudinary.meal_photos}/${r.cloudinary.meal_photos_authenticated} · backups ${r.cloudinary.backups}` : '—';

  return (
    <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="stage2-records-card">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h3 className="font-bold text-lg">Stage 2 — clinician test records (Q7, read-only dry run)</h3>
        <Button size="sm" className="rounded-full ml-auto" onClick={run} disabled={busy} data-testid="stage2-dry-run-btn">{busy ? 'Checking…' : rep ? 'Run again' : 'Dry run'}</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">The 20 Stage-2 identities and the a327c45e throwaway, looked up in this database. Nothing is written and there is no apply button: removal is Users → Delete, then Backups → Pending deletion → Purge, one account at a time, after the director approves each one. Audit, merge and purge records always stay.</p>
      {rep && (
        <>
          <p className="text-sm font-semibold" data-testid="stage2-summary">{rep.environment}: {rep.present} of {rep.listed} still present ({Object.entries(rep.present_by_status).map(([s, n]) => `${n} ${s}`).join(', ') || '—'}) · {rep.absent} absent</p>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs" data-testid="stage2-table">
              <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Account</th><th className="pr-2">What it is</th><th className="pr-2">Status</th><th className="pr-2">Created</th><th className="pr-2">Links (would be removed)</th><th className="pr-2">Cloudinary</th><th className="pr-2">Stays</th></tr></thead>
              <tbody>
                {rep.rows.map((r, i) => (
                  <tr key={i} className={`border-t border-border/40 ${r.status === 'absent' ? 'text-muted-foreground' : ''}`} data-testid={`stage2-row-${r.id_prefix || r.account_ref}`}>
                    <td className="py-1 pr-2 font-mono">{r.id_prefix || '—'} · {r.account_ref}</td>
                    <td className="pr-2">{r.kind}</td>
                    <td className="pr-2">{r.status}{r.already_purged_here ? ' (purged here)' : ''}</td>
                    <td className="pr-2">{r.created_at || '—'}</td>
                    <td className="pr-2">{r.status === 'absent' ? '—' : `users 1, ${links(r)}`}</td>
                    <td className="pr-2 font-mono">{cloud(r)}</td>
                    <td className="pr-2">{r.audit_rows_that_stay ? (Object.entries(r.audit_rows_that_stay).map(([k, v]) => `${k} ${v}`).join(', ') || 'none yet') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Compares: {rep.compares}</p>
        </>
      )}
    </div>
  );
};
